<div class="space-y-6">

  <!-- Loading Spinner -->
  <div *ngIf="isLoading" class="flex justify-center items-center py-24">
    <div class="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
  </div>

  <!-- Error State -->
  <div *ngIf="!isLoading && errorMessage"
    class="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-lg">
    <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-9.25a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 6a.75.75 0 100-1.5.75.75 0 000 1.5z"
        clip-rule="evenodd" />
    </svg>
    <span>{{ errorMessage }}</span>
  </div>

  <!-- Content -->
  <ng-container *ngIf="!isLoading && !errorMessage && mechanic">

    <!-- Profile Card -->
    <div class="bg-white rounded-2xl shadow p-6 flex flex-col sm:flex-row gap-6">
      <img
        [src]="avatarUrl"
        [alt]="mechanic.name"
        class="w-24 h-24 rounded-full object-cover ring-4 ring-blue-100 shrink-0" />

      <div class="flex-1">
        <div class="flex flex-wrap items-center gap-3 mb-1">
          <h2 class="text-2xl font-semibold text-gray-800">{{ mechanic.name }}</h2>
          <app-badge [color]="getBadgeColor(verificationStatus)">
            {{ verificationStatus }}
          </app-badge>
          <app-badge [color]="getBadgeColor(accountStatus)">
            {{ accountStatus }}
          </app-badge>
          <span
            class="px-2 py-0.5 text-xs rounded-full font-medium"
            [class.bg-green-100]="isAvailable"
            [class.text-green-700]="isAvailable"
            [class.bg-gray-100]="!isAvailable"
            [class.text-gray-500]="!isAvailable">
            {{ isAvailable ? 'Available' : 'Unavailable' }}
          </span>
        </div>

        <p class="text-gray-500 text-sm">{{ mechanic.email }}</p>
        <p class="text-gray-500 text-sm">{{ mechanic.phone }}</p>
        <p class="text-gray-500 text-sm">{{ mechanic.mechanic_profile?.area ?? '—' }}</p>

        <!-- Bio -->
        <p *ngIf="mechanic.mechanic_profile?.bio"
          class="mt-3 text-gray-600 text-sm">
          {{ mechanic.mechanic_profile!.bio }}
        </p>
      </div>
    </div>

    <!-- Stats Row -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <div class="bg-white rounded-2xl shadow p-5 text-center">
        <p class="text-3xl font-bold text-blue-600">{{ rating | number:'1.1-1' }}</p>
        <p class="text-sm text-gray-500 mt-1">Avg. Rating</p>
        <!-- Stars -->
        <div class="flex justify-center gap-0.5 mt-2">
          <ng-container *ngFor="let s of getStars(rating)">
            <svg class="w-4 h-4"
              [class.text-yellow-400]="s <= rating"
              [class.text-gray-200]="s > rating"
              fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </ng-container>
        </div>
      </div>

      <div class="bg-white rounded-2xl shadow p-5 text-center">
        <p class="text-3xl font-bold text-blue-600">{{ totalJobs }}</p>
        <p class="text-sm text-gray-500 mt-1">Total Jobs</p>
      </div>

      <div class="bg-white rounded-2xl shadow p-5 text-center">
        <p class="text-3xl font-bold text-blue-600">${{ totalEarnings | number:'1.0-0' }}</p>
        <p class="text-sm text-gray-500 mt-1">Total Earnings</p>
      </div>

      <div class="bg-white rounded-2xl shadow p-5 text-center">
        <p class="text-3xl font-bold text-blue-600">{{ experience }}</p>
        <p class="text-sm text-gray-500 mt-1">Years Exp.</p>
      </div>
    </div>

    <!-- Skillset -->
    <div class="bg-white rounded-2xl shadow p-6">
      <h3 class="text-lg font-semibold mb-4 text-gray-800">Skillset</h3>
      <div *ngIf="skills.length; else noSkills" class="flex flex-wrap gap-2">
        <span
          *ngFor="let skill of skills"
          class="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
          {{ skill }}
        </span>
      </div>
      <ng-template #noSkills>
        <p class="text-gray-400 text-sm">No skills listed.</p>
      </ng-template>
    </div>

    <!-- Working Hours -->
    <div *ngIf="mechanic.mechanic_profile?.working_hours"
      class="bg-white rounded-2xl shadow p-6">
      <h3 class="text-lg font-semibold mb-4 text-gray-800">Working Hours</h3>
      <ul class="space-y-2">
        <li
          *ngFor="let entry of mechanic.mechanic_profile!.working_hours! | keyvalue"
          class="flex justify-between text-sm text-gray-600">
          <span class="font-medium capitalize">{{ entry.key }}</span>
          <span>{{ entry.value }}</span>
        </li>
      </ul>
    </div>

    <!-- Meta Info -->
    <div class="bg-white rounded-2xl shadow p-6">
      <h3 class="text-lg font-semibold mb-4 text-gray-800">Account Info</h3>
      <dl class="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt class="text-gray-400">Member Since</dt>
          <dd class="text-gray-700 font-medium">{{ mechanic.created_at | date:'mediumDate' }}</dd>
        </div>
        <div>
          <dt class="text-gray-400">Last Updated</dt>
          <dd class="text-gray-700 font-medium">{{ mechanic.updated_at | date:'mediumDate' }}</dd>
        </div>
        <div>
          <dt class="text-gray-400">Role</dt>
          <dd class="text-gray-700 font-medium capitalize">{{ mechanic.role }}</dd>
        </div>
        <div>
          <dt class="text-gray-400">Mechanic ID</dt>
          <dd class="text-gray-700 font-medium">#{{ mechanic.id }}</dd>
        </div>
      </dl>
    </div>

  </ng-container>

</div>